import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function boldify(s) {
  return s.split(/(\*\*[^*]+\*\*|_[^_]+_)/g).map((p, j) => {
    if (p.startsWith("**")) return <strong key={j} className="font-semibold text-[#0B1729]">{p.slice(2, -2)}</strong>;
    if (p.startsWith("_") && p.endsWith("_") && p.length > 2) return <em key={j} className="text-[#1E2D44]/55">{p.slice(1, -1)}</em>;
    return <React.Fragment key={j}>{p}</React.Fragment>;
  });
}

function renderMd(text) {
  return (text || "").split("\n").map((ln, i) => {
    if (/^\s*[-*]\s/.test(ln))
      return <li key={i} className="ml-4 list-disc text-sm leading-relaxed text-[#1E2D44]/85">{boldify(ln.replace(/^\s*[-*]\s/, ""))}</li>;
    if (ln.trim() === "") return <div key={i} className="h-1.5" />;
    return <p key={i} className="text-sm leading-relaxed text-[#1E2D44]/85">{boldify(ln)}</p>;
  });
}

const OP_LABELS = {
  complete_action: "Complete action",
  update_action_status: "Update action status",
  delete_action: "Delete action",
};

export function AskAmdenWidget() {
  const { orgId } = useOrg();
  const { user, isAmden, canEdit } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    if (!orgId) { setMessages([]); return; }
    api.get(`/ai/assist/history?org_id=${orgId}`)
      .then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content, executed: m.executed }))))
      .catch(() => {});
  }, [orgId]);

  const needsClient = isAmden && !orgId;

  const clearChat = async () => {
    if (!orgId) return;
    try { await api.delete(`/ai/assist/history?org_id=${orgId}`); setMessages([]); }
    catch (e) { /* noop */ }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || !orgId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/assist?org_id=${orgId}`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, executed: data.executed, pending: data.pending }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach the coach just now. Please try again shortly." }]);
    } finally {
      setBusy(false);
    }
  };

  const confirmPending = async (idx, pending) => {
    try {
      const { data } = await api.post(`/ai/assist/execute?org_id=${orgId}`, { type: pending.type, params: pending.params });
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, pending: null, executed: data.executed, content: "Done — the change has been applied." } : x)));
      toast.success("Change applied");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const cancelPending = (idx) => {
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, pending: null, content: x.content + "\n\n_No changes made._" } : x)));
  };

  const goTo = (link) => { if (link) { setOpen(false); navigate(link); } };

  const suggestions = canEdit
    ? ["What should I focus on this week?", "Add an action to follow up with a key client by Friday", "Log an issue about our overdue billing"]
    : ["What should I focus on this week?", "Summarise my overdue actions", "How is my plan tracking?"];

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ask-widget-button"
        aria-label="Ask Amden"
        className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#0B1729] text-[#FFC24D] shadow-xl ring-1 ring-white/10 transition-transform hover:scale-105 active:scale-95 lg:bottom-6 lg:right-6"
      >
        {open ? <Icons.X size={22} /> : <Icons.Sparkles size={22} />}
        {!open && <span className="absolute right-0 top-0 flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFB000] opacity-60" /><span className="relative inline-flex h-3 w-3 rounded-full bg-[#FFB000]" /></span>}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="ask-widget-panel"
          className="fixed bottom-36 right-4 z-50 flex h-[68vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-[#E4E8EF] bg-white shadow-2xl lg:bottom-24 lg:right-6"
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 bg-[#0B1729] px-4 py-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFB000]"><Icons.Sparkles size={16} className="text-[#0B1729]" /></span>
            <div className="flex-1">
              <div className="font-display text-sm font-bold text-white">Ask Amden</div>
              <div className="text-[11px] text-white/45">Quick help · ask or get things done</div>
            </div>
            {messages.length > 0 && !needsClient && (
              <button onClick={clearChat} data-testid="ask-widget-clear" title="Start a new chat" className="text-white/50 hover:text-white"><Icons.RotateCcw size={16} /></button>
            )}
            <button onClick={() => setOpen(false)} data-testid="ask-widget-close" className="text-white/50 hover:text-white"><Icons.X size={18} /></button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-[#F9FAFC] px-3.5 py-4">
            {needsClient ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <Icons.Building2 size={26} className="mb-2 text-[#1E2D44]/30" />
                <p className="text-sm text-[#1E2D44]/60">Pick a client from the switcher above to ask Amden about their business.</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col justify-center">
                <div className="mb-4 text-center">
                  <span className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B1729]"><Icons.Sparkles size={20} className="text-[#FFC24D]" /></span>
                  <p className="text-sm font-semibold text-[#0B1729]">How can I help, {user?.name?.split(" ")[0]}?</p>
                  <p className="mt-1 text-xs text-[#1E2D44]/55">{canEdit ? "Ask a question — or tell me to add an action, priority or issue." : "Ask me anything about your business."}</p>
                </div>
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ask-widget-suggestion" className="w-full rounded-lg border border-[#E4E8EF] bg-white px-3 py-2 text-left text-xs text-[#1E2D44]/75 transition-colors hover:border-[#FFC24D] hover:text-[#FFB000]">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} data-testid="ask-widget-message" className={m.role === "user" ? "flex justify-end" : ""}>
                  {m.role === "user" ? (
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#0B1729] px-3.5 py-2 text-sm text-white">{m.content}</div>
                  ) : (
                    <div className="max-w-[92%]">
                      <div className="rounded-2xl rounded-tl-sm border border-[#E4E8EF] bg-white px-3.5 py-2.5">{renderMd(m.content)}</div>
                      {m.executed && (
                        <button
                          onClick={() => goTo(m.executed.link)}
                          data-testid="ask-widget-executed"
                          className="mt-2 flex w-full items-center gap-2 rounded-lg border border-[#3D8B65]/25 bg-[#3D8B65]/8 px-3 py-2 text-left text-xs transition-colors hover:bg-[#3D8B65]/12"
                        >
                          <Icons.CheckCircle2 size={15} className="shrink-0 text-[#3D8B65]" />
                          <span className="flex-1 text-[#1E2D44]/85">{m.executed.deleted ? "Deleted" : "Created"} {m.executed.kind}: <span className="font-semibold text-[#0B1729]">{m.executed.title}</span></span>
                          {m.executed.link && !m.executed.deleted && <Icons.ArrowUpRight size={14} className="shrink-0 text-[#1E2D44]/40" />}
                        </button>
                      )}
                      {m.pending && (
                        <div className="mt-2 rounded-lg border border-[#FFB000]/40 bg-[#FFB000]/8 p-3" data-testid="ask-widget-pending">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#B87F1E]"><Icons.AlertTriangle size={13} /> Confirm change</div>
                          <p className="mt-1 text-xs text-[#1E2D44]/80">{m.pending.summary || OP_LABELS[m.pending.type] || "Apply this change?"}</p>
                          <div className="mt-2.5 flex gap-2">
                            <Button onClick={() => confirmPending(i, m.pending)} size="sm" className="h-8 flex-1 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="ask-widget-confirm">Confirm</Button>
                            <Button onClick={() => cancelPending(i)} size="sm" variant="outline" className="h-8 flex-1" data-testid="ask-widget-cancel">Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            {busy && (
              <div className="flex items-center gap-1.5 px-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.15s" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.3s" }} />
              </div>
            )}
          </div>

          {/* Composer */}
          {!needsClient && (
            <div className="border-t border-[#E4E8EF] bg-white p-2.5">
              <div className="flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask a question or give an instruction…"
                  rows={1}
                  className="max-h-24 min-h-[40px] resize-none bg-[#F9FAFC] text-sm"
                  data-testid="ask-widget-input"
                />
                <Button onClick={() => send()} disabled={busy || !input.trim()} className="h-10 w-10 shrink-0 bg-[#FFB000] p-0 text-[#0B1729] hover:bg-[#E69E00]" data-testid="ask-widget-send">
                  {busy ? <Icons.Loader2 className="animate-spin" size={16} /> : <Icons.ArrowUp size={16} />}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
